import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';

// icons
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

// styling
import { Colors, Spacing } from '_styles';

const CollapsibleChevron = ({ isExpanded, onToggle, size = 18 }) => {
  return (
    <FaChevronDown
      onClick={onToggle}
      size={size}
      color={Colors.DARK_GREY}
      style={{
        cursor: 'pointer',
        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.25s ease',
      }}
    />
  );
};

const CollapsibleComponent = ({ collapsable, setCollapsed, hideChevron, expanded, onToggle, children }) => {
  const isControlled = expanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = isControlled ? expanded : internalExpanded;

  useEffect(() => {
    if (!isControlled && collapsable) {
      setCollapsed?.(true);
      setInternalExpanded(true);
    }
  }, [collapsable])

  const toggleCollapse = () => {
    if (isControlled) {
      onToggle?.();
    } else {
      setCollapsed?.(!isExpanded);
      setInternalExpanded(!isExpanded);
    }
  };

  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(isExpanded ? 'none' : '0px');
  const prevExpandedRef = useRef(isExpanded);

  // useLayoutEffect: measure after DOM update, before paint.
  // With max-height:0 + overflow:hidden, flex children can lay out tiny — scrollHeight is wrong (~8px).
  // Temporarily set maxHeight to 'none' so content reaches its true height before reading scrollHeight.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const wasExpanded = prevExpandedRef.current;
    prevExpandedRef.current = isExpanded;

    if (isExpanded) {
      if (!wasExpanded) {
        el.style.maxHeight = 'none';
        const targetHeight = el.scrollHeight;
        el.style.maxHeight = '0px';
        void el.offsetHeight;
        setMaxHeight(`${targetHeight}px`);
        const onEnd = () => setMaxHeight('none');
        el.addEventListener('transitionend', onEnd, { once: true });
        return () => el.removeEventListener('transitionend', onEnd);
      }
    } else if (wasExpanded) {
      el.style.maxHeight = 'none';
      const h = el.scrollHeight;
      el.style.maxHeight = `${h}px`;
      void el.offsetHeight;
      setMaxHeight(`${h}px`);
      requestAnimationFrame(() => setMaxHeight('0px'));
    }
  }, [isExpanded]);

  return (
    <div style={{}}>
      {!hideChevron && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            cursor: 'pointer',
          }}
        >
          {collapsable ? (
            <CollapsibleChevron isExpanded={isExpanded} onToggle={toggleCollapse} />
          ) : null}
        </div>
      )}
      <div
        ref={contentRef}
        style={{
          maxHeight,
          overflow: 'hidden',
          transition: maxHeight === 'none' ? 'none' : 'max-height 0.3s ease',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          {children}
        </div>
      </div>
    </div>
  );
};


export { CollapsibleChevron };
export default CollapsibleComponent;
